import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

// Yup validation schema
const validationSchema = Yup.object({
  firstName: Yup.string().required("This field is required."),
  lastName: Yup.string().required("This field is required."),
  email: Yup.string()
    .email("Invalid email address")
    .required("This field is required."),
  phone: Yup.string().required("This field is required."),
  companyName: Yup.string().required("This field is required."),
  businessType: Yup.string().required("This field is required."),
  country: Yup.string().required("This field is required."),
  callTime: Yup.string().required("This field is required."),
});

const businessTypes = [
  "Everything else",
  "Hair",
  "Beauty",
  "Massage/Day Spa",
  "Group Fitness",
  "Health/Wellbeing Clinic",
  "Tutoring/Consulting",
  "Hair and Beauty",
  "Barber",
  "Body Art",
  "Personal Training",
];

const callTimes = [
  "8am",
  "9am",
  "10am",
  "11am",
  "12pm",
  "1pm",
  "2pm",
  "3pm",
  "4pm",
  "5pm",
  "6pm",
  "7pm",
];

const RequestCall = () => {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const timer = setTimeout(() => {
      setShowForm(true);
    }, 1500); // Delay 1.5s (you can adjust this)

    return () => clearTimeout(timer);
  }, []);
  return (
    <section className="min-h-screen bg-dry flex items-start justify-center pt-32 py-16 px-0">
      <div
        className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12"
        data-aos="fade-up"
      >
        {/* Image section */}
        <div className="order-first lg:order-last">
          <div className="sticky top-24 self-start">
            <img
              src="/images/galery/img_57.jpg"
              alt="Request Call"
              className="rounded-xl shadow-lg w-full h-[350px] md:h-auto object-cover grayscale p-4"
            />
          </div>
        </div>

        {/* Left: Form */}
        <div className="bg-transparent rounded-xl shadow p-8 order-last lg:order-first ">
          <h2 className="text-3xl font-semibold text-center mb-2 text-text">
            Let’s Talk
          </h2>
          <p className="text-center text-textGray mb-8">
            Have questions? We're here to help. Choose a time that works for
            you, and we'll chat about how Lokalai can support your business.
          </p>

          <div>
            {!showForm ? (
              <div className="w-full h-full bg-dry border border-border rounded-lg animate-pulse min-h-[60px]"></div>
            ) : (
              <div className="border border-border rounded-lg p-6">
                <Formik
                  initialValues={{
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    companyName: "",
                    businessType: "",
                    country: "",
                    callTime: "",
                    emailMarketing: false,
                    smsMarketing: false,
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { resetForm }) => {
                    console.log("Form submitted:", values);
                    resetForm();
                    alert("Thank you! We will contact you shortly.");
                  }}
                >
                  {({ errors, touched }) => (
                    <Form className="space-y-4">
                      {[
                        "firstName",
                        "lastName",
                        "email",
                        "phone",
                        "companyName",
                      ].map((field) => (
                        <div key={field}>
                          <Field
                            name={field}
                            type="text"
                            placeholder={`${field.replace(
                              /([A-Z])/g,
                              " $1"
                            )}: *`}
                            className={`w-full border border-border rounded-lg bg-dry text-text placeholder-textGray p-3 focus:outline-none focus:ring-2 ${
                              errors[field] && touched[field]
                                ? "border-red-400"
                                : "focus:ring-red-300"
                            }`}
                          />

                          <ErrorMessage
                            name={field}
                            component="div"
                            className="text-red-500 text-[12px] mt-1"
                          />
                        </div>
                      ))}

                      {/* Select: Business Type */}
                      <div>
                        <Field
                          as="select"
                          name="businessType"
                          className={`w-full border border-border rounded-lg bg-dry text-textGray p-3 focus:outline-none focus:ring-2 ${
                            errors.businessType && touched.businessType
                              ? "border-red-400"
                              : "focus:ring-red-300"
                          }`}
                        >
                          <option value="">Business Type: *</option>
                          {businessTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="businessType"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Select: Country */}
                      <div>
                        <Field
                          as="select"
                          name="country"
                          className={`w-full border border-border bg-dry text-textGray rounded-lg p-3 focus:outline-none focus:ring-2 ${
                            errors.country && touched.country
                              ? "border-red-400"
                              : "focus:ring-red-300"
                          }`}
                        >
                          <option value="">Country: *</option>
                          <option value="New Zealand">New Zealand</option>
                          <option value="Australia">Australia</option>
                          <option value="Canada">Canada</option>
                        </Field>
                        <ErrorMessage
                          name="country"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Select: Preferred Call Time */}
                      <div>
                        <Field
                          as="select"
                          name="callTime"
                          className={`w-full border border-border bg-dry text-textGray rounded-lg p-3 focus:outline-none focus:ring-2 ${
                            errors.callTime && touched.callTime
                              ? "border-red-400"
                              : "focus:ring-red-300"
                          }`}
                        >
                          <option value="">Preferred Call Time *</option>
                          {callTimes.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="callTime"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {/* Checkboxes */}
                      <div className="flex items-center gap-3">
                        <Field
                          type="checkbox"
                          name="emailMarketing"
                          className="w-5 h-5"
                        />
                        <label className="text-sm text-textGray">
                          Email me Lokalai news and marketing offers
                        </label>
                      </div>

                      {/* Legal text */}
                      <p className="text-xs text-gray-500">
                        <a href="#" className="underline">
                          Privacy Policy
                        </a>{" "}
                        and{" "}
                        <a href="#" className="underline">
                          Terms of Service
                        </a>{" "}
                        apply.
                      </p>

                      <button
                        type="submit"
                        className="w-full bg-redlight hover:text-reddark text-white font-semibold py-3 rounded-full mt-4"
                      >
                        Request a call
                      </button>
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestCall;
